import {
  distance,
  DistanceUnit,
  ExerciseType,
  weight,
  exhaustiveTypeCheck,
  WeightUnit,
} from 'hevy-shared';
import { formatNumber, formatNumberWithUnit } from './formatters';

/**
 * 0min
 * 14min
 * 2h 4min
 * 2h 0min
 */
export const secondsToWordFormatMinutesLocalized = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);

  const formattedHours = formatNumber(hours, {
    style: 'unit',
    unit: 'hour',
    unitDisplay: 'narrow',
  });
  const formattedMins = formatNumber(minutes, {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'narrow',
  });

  if (hours) {
    return `${formattedHours} ${formattedMins}`;
  }

  return formattedMins;
};

/**
 * 10s
 * 1min 10s
 * 2h 4min 45s
 */
export const secondsToWordFormatLocalized = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - hours * 3600) / 60);
  const remainderSeconds = Math.floor(seconds % 60);

  const formattedHours = formatNumberWithUnit(hours, 'hours', {
    unitDisplay: 'narrow',
  });
  const formattedMins = formatNumberWithUnit(minutes, 'minutes', {
    unitDisplay: 'narrow',
  });
  // formatNumberWithUnit uses this function to format seconds instead of
  // Intl.NumberFormat since all (so far) instances of displayed seconds
  // use this format
  const formattedSecs = formatNumber(remainderSeconds, {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'narrow',
  });

  if (hours) {
    return `${formattedHours} ${formattedMins} ${formattedSecs}`;
  }

  if (minutes) {
    return `${formattedMins} ${formattedSecs}`;
  }

  return formattedSecs;
};

type GetSetValueParams = {
  exerciseType: ExerciseType;
  set: {
    weight_kg: number | null;
    reps: number | null;
    duration_seconds: number | null;
    distance_meters: number | null;
    custom_metric: number | null;
  };
  units: {
    weight: WeightUnit;
    distance: DistanceUnit;
  };
};
export const formatSetValue = ({ exerciseType, set, units }: GetSetValueParams) => {
  const distanceUnitShort = units.distance === 'kilometers' ? 'km' : 'mi';

  switch (exerciseType) {
    case 'weight_reps':
    case 'bodyweight_reps': {
      const weightLabel = formatNumberWithUnit(
        weight(set.weight_kg || 0, units.weight),
        units.weight,
      );
      const reps = `${set.reps ?? 0}`;
      return `${weightLabel} x ${reps}`;
    }

    case 'bodyweight_assisted_reps': {
      const weightLabel = formatNumberWithUnit(
        -1 * weight(set.weight_kg || 0, units.weight),
        units.weight,
      );
      const reps = `${set.reps ?? 0}`;
      return `${weightLabel} x ${reps}`;
    }

    case 'reps_only':
      return `${set.reps ?? 0}`;

    case 'distance_duration': {
      const distanceValue = formatNumberWithUnit(
        distance(set.distance_meters || 0, units.distance),
        distanceUnitShort,
      );
      const duration = `${secondsToWordFormatLocalized(Number(set.duration_seconds))}`;
      return `${distanceValue} - ${duration}`;
    }

    case 'duration':
      return secondsToWordFormatLocalized(Number(set.duration_seconds));

    case 'short_distance_weight': {
      const weightLabel = formatNumberWithUnit(
        weight(set.weight_kg || 0, units.weight),
        units.weight,
      );

      const distanceValue = formatNumberWithUnit(
        set.distance_meters || 0,
        units.distance === 'kilometers' ? 'm' : 'yd',
        {
          maximumFractionDigits: 2,
        },
      );

      return `${weightLabel} - ${distanceValue}`;
    }

    case 'weight_duration': {
      const weightLabel = formatNumberWithUnit(
        weight(set.weight_kg || 0, units.weight),
        units.weight,
      );
      const duration = `${secondsToWordFormatLocalized(Number(set.duration_seconds))}`;
      return `${weightLabel} - ${duration}`;
    }

    case 'floors_duration':
      const floors = formatNumberWithUnit(set.custom_metric || 0, 'floors', {
        maximumFractionDigits: 0,
      });
      return `${floors} - ${secondsToWordFormatLocalized(Number(set.duration_seconds))}`;

    case 'steps_duration':
      const steps = formatNumberWithUnit(set.custom_metric || 0, 'steps', {
        maximumFractionDigits: 0,
      });
      return `${steps} - ${secondsToWordFormatLocalized(Number(set.duration_seconds))}`;
    default:
      exhaustiveTypeCheck(exerciseType);
      return '';
  }
};
